import {APIClient} from '../utils/APIClient';
import {API_URL} from '../settings/local_settings';

// Capa API de Media Plan Sync (propuesta desde la planilla del media plan). Espeja los endpoints
// del backend bajo `agent/media-plan/`. Las conexiones se manejan con ClientMemoryApi
// (source-connection con purpose=MEDIA_PLAN).
const BASE = API_URL + 'agent/media-plan/';

export const MediaPlanApi = {
    getProposal(agencyClientId, connectionId) {
        const url = `${BASE}proposal/?agency_client_id=${encodeURIComponent(agencyClientId)}` +
            `&connection_id=${encodeURIComponent(connectionId)}`;
        return new APIClient(url).call('GET');
    },

    getInterpretation(proposalId, agencyClientId) {
        return new APIClient(`${BASE}proposal/${proposalId}/interpretation/`
            + `?agency_client_id=${encodeURIComponent(agencyClientId)}`).call('GET');
    },

    saveInterpretation(proposalId, agencyClientId, interpretation) {
        return new APIClient(`${BASE}proposal/${proposalId}/interpretation/`).call('POST', {
            ...interpretation, agency_client_id: agencyClientId,
        });
    },

    // Sincrónico: lee la planilla + LLM (~10-15s). El caller muestra spinner.
    generate(agencyClientId, connectionId, force = false) {
        return new APIClient(`${BASE}generate/`).call('POST', {
            agency_client_id: agencyClientId, connection_id: connectionId, force,
        });
    },

    reviewItem(itemId, agencyClientId, action, payload = null) {
        const body = {agency_client_id: agencyClientId, action};
        if (payload) body.payload = payload;
        return new APIClient(`${BASE}item/${itemId}/review/`).call('POST', body);
    },

    answerItem(itemId, agencyClientId, answerKey, answerText = null) {
        return new APIClient(`${BASE}item/${itemId}/answer/`).call('POST', {
            agency_client_id: agencyClientId, answer_key: answerKey, answer_text: answerText,
        });
    },

    // Resuelve un aviso de conflicto de objetivo (CONFLICT · GOAL_MISMATCH, Fase B): `resolution` ∈
    // {'use_plan','keep_mine'} (radio A/B de la card ámbar). NO regenera; el caller recarga la propuesta.
    resolveConflict(itemId, agencyClientId, resolution) {
        return new APIClient(`${BASE}item/${itemId}/resolve-conflict/`).call('POST', {
            agency_client_id: agencyClientId, resolution,
        });
    },

    // Responde N dudas de alias de una y regenera la propuesta UNA sola vez (confirmar N sugeridas).
    // `answers` = [{item_id, answer_key}]. Todo-o-nada: un answer inválido → 400 sin pinnear nada.
    bulkAnswer(proposalId, agencyClientId, answers) {
        return new APIClient(`${BASE}proposal/${proposalId}/answers/`).call('POST', {
            agency_client_id: agencyClientId, answers,
        });
    },

    // Vecindario de UNA celda del grid leído, para previsualizar la evidencia sin salir de Dashbo
    // (`CellPeek`). Sale del SNAPSHOT, no de la planilla en vivo: es lo que la IA leyó al armar la
    // propuesta. Respuesta chica (~1-3 KB) y estable — el snapshot es inmutable.
    cellExcerpt(agencyClientId, snapshotId, cell, pad = null) {
        const padParam = pad === null || pad === undefined ? '' : `&pad=${encodeURIComponent(pad)}`;
        const url = `${BASE}snapshot/${encodeURIComponent(snapshotId)}/cell-excerpt/` +
            `?agency_client_id=${encodeURIComponent(agencyClientId)}` +
            `&cell=${encodeURIComponent(cell)}${padParam}`;
        return new APIClient(url).call('GET');
    },

    apply(proposalId, agencyClientId) {
        return new APIClient(`${BASE}proposal/${proposalId}/apply/`).call('POST', {
            agency_client_id: agencyClientId,
        });
    },

    // P1: deshace la DECISIÓN de destino de una línea (pin `bu_for::<line_key>`: vinculada o
    // "crear nueva") y regenera. Anclado a (connection, line_key) — en "vinculada y al día" no
    // existe item. NO revierte cambios ya aplicados (solo corta el gobierno hacia adelante).
    undoLink(agencyClientId, connectionId, lineKey) {
        return new APIClient(`${BASE}link/undo/`).call('POST', {
            agency_client_id: agencyClientId, connection_id: connectionId, line_key: lineKey,
        });
    },

    // UD-2: fija la moneda del plan (pin de la conexión) y regenera sin IA. `currency` null = volver a
    // la decisión automática. Devuelve la propuesta nueva. Lo usan el «Cambiar» del encabezado, la
    // pregunta de moneda y la salida del conflicto de moneda («El plan está en COP»).
    setPlanCurrency(agencyClientId, connectionId, currency) {
        return new APIClient(`${BASE}currency/`).call('POST', {
            agency_client_id: agencyClientId, connection_id: connectionId, currency,
        });
    },

    // Base de los montos (tanda «grupos, dinero y DV360»): fija qué columna de UNA tabla del plan es la
    // inversión en plataforma (pin `basis_for::<firma>`) y regenera sin IA. Responde como la moneda:
    // `{status, proposal}`.
    setPlanBasis(agencyClientId, connectionId, tableSignature, column) {
        return new APIClient(`${BASE}basis/`).call('POST', {
            agency_client_id: agencyClientId, connection_id: connectionId,
            table_signature: tableSignature, column,
        });
    },

    // Reparto grupo del plan → línea, guardado por conexión. La pantalla lo LEE de la propuesta
    // (`proposal.group_mapping`); el backend también tiene un GET con la misma forma, que la pantalla no usa.
    // `mapping` = {<group_key>: {target: 'BU'|'NEW'|'UNTRACKED', bu_id?, name?}}. El backend mergea con
    // lo guardado (no borra los grupos que no vienen) y regenera sin IA: responde `{status, proposal}`.
    saveGroupMapping(agencyClientId, connectionId, mapping) {
        return new APIClient(`${BASE}group-mapping/`).call('POST', {
            agency_client_id: agencyClientId, connection_id: connectionId, mapping,
        });
    },

    // Escaneo de solapas: cuál se está leyendo, cuáles compiten y por qué se descartó cada una.
    // Sin `refresh` sale del escaneo persistido (instantáneo, sin tocar Google); con `refresh` se
    // re-listan las solapas del archivo SIN parsear — hace falta para ver la solapa del mes nuevo
    // apenas la crean. OJO: la respuesta de éxito viene PELADA (sin `status`): se reconoce por su
    // forma (`isTabsPayload` de `tabStrip.js`), no con `status === 'OK'`.
    getTabs(agencyClientId, connectionId, refresh = false) {
        const url = `${BASE}tabs/?agency_client_id=${encodeURIComponent(agencyClientId)}` +
            `&connection_id=${encodeURIComponent(connectionId)}${refresh ? '&refresh=1' : ''}`;
        return new APIClient(url).call('GET');
    },

    // Fija a mano la solapa del plan de un período, o vuelve a la elección automática (`tabGid`
    // null), y relee. El pin es POR MES a propósito: se autoexpira, así que el mes que viene Dashbo
    // vuelve a elegir solo en vez de seguir leyendo el plan viejo en silencio.
    selectTab(agencyClientId, connectionId, tabGid, month = null) {
        const body = {agency_client_id: agencyClientId, connection_id: connectionId,
                      tab_gid: tabGid === undefined ? null : tabGid,
                      // El backend valida "mes actual o próximo": sin el offset lo calcularía en UTC
                      // y, pasadas las 21 ART del último día del mes, rechazaría la solapa que el
                      // usuario tiene delante. La fecha de negocio es la suya, no la del servidor.
                      tz_offset_minutes: new Date().getTimezoneOffset()};
        if (month) body.month = month;
        return new APIClient(`${BASE}tab/select/`).call('POST', body);
    },
};
